'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Direction = 'encode' | 'decode';
type Base = 'binary' | 'hex' | 'decimal';

function encodeText(text: string, base: Base): string {
  const bytes = new TextEncoder().encode(text);
  return [...bytes]
    .map((byte) => {
      if (base === 'binary') return byte.toString(2).padStart(8, '0');
      if (base === 'hex') return byte.toString(16).padStart(2, '0');
      return String(byte);
    })
    .join(' ');
}

function decodeText(input: string, base: Base): { text: string; error: string | null } {
  const tokens = input
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);
  if (!tokens.length) return { text: '', error: null };
  const radix = base === 'binary' ? 2 : base === 'hex' ? 16 : 10;
  const bytes: number[] = [];
  for (const token of tokens) {
    const value = parseInt(token, radix);
    if (Number.isNaN(value) || value < 0 || value > 255) {
      return { text: '', error: `"${token}" is not a valid ${base} byte.` };
    }
    bytes.push(value);
  }
  try {
    return {
      text: new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(bytes)),
      error: null,
    };
  } catch {
    return { text: '', error: 'Those bytes are not valid UTF-8 text.' };
  }
}

export default function BrowserBinaryTranslator() {
  const usageReported = useRef(false);
  const [direction, setDirection] = useState<Direction>('encode');
  const [base, setBase] = useState<Base>('binary');
  const [input, setInput] = useState('Hello');
  const [copied, setCopied] = useState(false);

  const output = useMemo(() => {
    if (direction === 'encode') return { text: encodeText(input, base), error: null };
    return decodeText(input, base);
  }, [input, direction, base]);

  useEffect(() => {
    if (!usageReported.current && input.trim().length > 0) {
      usageReported.current = true;
      void consumeToolUsage('binary-translator', '').catch(() => undefined);
    }
  }, [input]);

  async function copyOutput() {
    try {
      await navigator.clipboard.writeText(output.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Translate text to binary and back</h2>
          <p>
            UTF-8 bytes in binary, hex, or decimal, translated live in both directions. Everything
            runs in your browser.
          </p>
        </div>
        <span className="tool-live-badge">01</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader" style={{ gap: '0.75rem' }}>
          <label style={{ width: '100%' }}>
            {direction === 'encode' ? 'Your text' : `Your ${base} bytes`}
            <textarea
              onChange={(event) => setInput(event.target.value)}
              rows={4}
              spellCheck={false}
              value={input}
            />
          </label>

          <label style={{ width: '100%' }}>
            {direction === 'encode' ? `As ${base}` : 'As text'}
            <textarea readOnly rows={5} value={output.error ?? output.text} />
          </label>

          <button
            className="button button-ghost"
            disabled={!!output.error}
            onClick={() => void copyOutput()}
            type="button"
          >
            {copied ? 'Copied!' : 'Copy result'}
          </button>
        </div>

        <aside className="tool-live-panel">
          <label>
            Direction
            <select
              onChange={(event) => setDirection(event.target.value as Direction)}
              value={direction}
            >
              <option value="encode">Text to bytes</option>
              <option value="decode">Bytes to text</option>
            </select>
          </label>
          <label>
            Byte format
            <select onChange={(event) => setBase(event.target.value as Base)} value={base}>
              <option value="binary">Binary (8 bits)</option>
              <option value="hex">Hexadecimal</option>
              <option value="decimal">Decimal</option>
            </select>
          </label>
          <p className="tool-live-panel-note">
            Text is encoded as UTF-8, so emoji and accents work: they simply take more than one byte
            per character, which is why the binary for é is longer than for e.
          </p>
        </aside>
      </div>
    </article>
  );
}
